import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDay, formatTime, templateLabel } from "./lib/format";
import { setsWord, stateBadge } from "./lib/history";

/**
 * One session in History — the list's rows and the calendar's chosen day
 * alike: the whole row is the link to it. The day and time it started in
 * the settings' zone, its template, its state and its set count; "via an
 * agent" when an agent started it.
 */
function HistoryRow({ row, timeZone, from }) {
  const badge = stateBadge(row.session_state);
  const day = formatDay(row.started_at, timeZone);
  const time = formatTime(row.started_at, timeZone);
  const byAgent = row.source && row.source !== "ui";
  return (
    <li>
      <Link
        to={`/training/history/${row.id}`}
        state={{ historySearch: from }}
        className="flex min-h-11 items-center gap-3 rounded-lg border bg-card p-4 text-foreground transition-colors hover:border-primary/60 focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-display text-lg font-semibold">{templateLabel(row.template)}</span>{" "}
          <span className="text-sm text-muted-foreground tabular-nums">
            <time dateTime={row.started_at}>
              {day}
              {time ? `, ${time}` : ""}
            </time>{" "}
            · {setsWord(row.set_count ?? 0)}
          </span>
        </span>{" "}
        <span className="flex shrink-0 flex-col items-end gap-1">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {byAgent && (
            <>
              {" "}
              <Badge variant="outline">via an agent</Badge>
            </>
          )}
        </span>
        <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}

HistoryRow.propTypes = {
  /** A TrainingSessionRow. */
  row: PropTypes.shape({
    id: PropTypes.number.isRequired,
    template: PropTypes.string,
    started_at: PropTypes.string,
    session_state: PropTypes.string,
    set_count: PropTypes.number,
    source: PropTypes.string,
  }).isRequired,
  timeZone: PropTypes.string,
  /** History's own query (`?page=2`), so the session can link back to it. */
  from: PropTypes.string,
};

export default HistoryRow;
